// core/interceptor/auth-interceptor.ts
import { HttpErrorResponse, HttpInterceptorFn, HttpContext } from '@angular/common/http';
import { inject } from '@angular/core';
import { SpinnerService } from '../services/spinner.service';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '../auth/auth.service';
import { catchError, finalize, throwError } from 'rxjs';
import { SKIP_SPINNER } from './spinner.tokens';
import { SKIP_AUTH } from './http-context-tokens';
import { environment } from '../../../environments/environment';

export const TOKEN = 'x-token';

// Rutas que NO muestran spinner
const EXCLUDED_SPINNER = [
    '/login',
    // Descargas largas
    '/tarjeta/reportes/linea/pdf',
    '/tarjeta/reportes/linea/excel'
];

// Rutas de TU backend que no requieren auth (ajustadas a prefijo /usuario/...)
const BYPASS_AUTH_PATHS = [
    '/api/sistema-datos',
    '/auth/login',
    '/auth/refresh',
    '/auth/forgot',
    '/auth/reset',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/usuario/auth/login',
    '/usuario/auth/refresh',
    '/usuario/auth/forgot',
    '/usuario/auth/reset',
    '/usuario/auth/forgot-password',
    '/usuario/auth/reset-password'
];

// *** Ajustá esto a tu backend real ***
const API_BASE = environment.url ?? ''; // ej: 'https://tu-backend.com/api'

function isOurApi(url: string): boolean {
    if (API_BASE) {
        try {
            const requestUrl = new URL(url, window.location.origin);
            const apiUrl = new URL(API_BASE, window.location.origin);
            if (requestUrl.origin !== apiUrl.origin) return false;

            const apiPath = apiUrl.pathname.endsWith('/') ? apiUrl.pathname : `${apiUrl.pathname}/`;
            return requestUrl.pathname.startsWith(apiPath);
        } catch {
            return false;
        }
    }

    // Si no hay API_BASE, no asumimos que una URL relativa sea backend.
    try {
        const requestUrl = new URL(url, window.location.origin);
        return requestUrl.origin === window.location.origin && requestUrl.pathname.startsWith('/api/');
    } catch {
        return false;
    }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const spinner = inject(SpinnerService);
    const auth = inject(AuthService);
    const cookie = inject(CookieService);

    // Normalizá pathname solo si aplica (si es absoluta)
    let pathname = '';
    try {
        pathname = new URL(req.url, window.location.origin).pathname.toLowerCase();
    } catch {
        pathname = req.url.toLowerCase();
    }

    // Flags
    const skipSpinner = req.context.get(SKIP_SPINNER) || EXCLUDED_SPINNER.some((u) => pathname.includes(u));
    const skipAuthByContext = req.context.get(SKIP_AUTH) === true;

    // ¿Esta URL es de nuestro backend?
    const ours = isOurApi(req.url);

    // Si NO es nuestra API, o si el caller pidió skip con contexto, NO metas auth ni fuerces token
    if (!ours || skipAuthByContext) {
        if (!skipSpinner) spinner.requestStarted();
        return next(req).pipe(
            finalize(() => {
                if (!skipSpinner) spinner.requestFinished();
            })
        );
    }

    // Desde acá, SOLO URLs de NUESTRA API
    const bypassAuth = BYPASS_AUTH_PATHS.some((u) => pathname.includes(u));

    if (bypassAuth) {
        if (!skipSpinner) spinner.requestStarted();
        return next(req).pipe(
            finalize(() => {
                if (!skipSpinner) spinner.requestFinished();
            })
        );
    }

    // Rutas protegidas de nuestra API → exige token
    const token = auth.getToken() || cookie.get(TOKEN);
    if (!token) {
        // No desloguees por una request externa; acá ya sabemos que es nuestra API
        if (!skipSpinner) spinner.requestFinished();
        return restrict(auth, 'No se proveyó el TOKEN', true);
    }

    const request = req.clone({
        setHeaders: { authorization: `Bearer ${token}` }
        // withCredentials: true // solo si TU backend lo necesita
    });

    if (!skipSpinner) spinner.requestStarted();

    return next(request).pipe(
        catchError((err: any) => {
            if (!skipSpinner) spinner.requestFinished();
            // Solo para nuestra API aplican estos códigos
            const CODES = [401, 403];
            if (err instanceof HttpErrorResponse && CODES.includes(err.status)) {
                return restrict(auth, 'No autorizado', true);
            }
            return throwError(() => err);
        }),
        finalize(() => {
            if (!skipSpinner) spinner.requestFinished();
        })
    );
};

const restrict = (auth: AuthService, msg: any, logout: boolean) => {
    if (logout) auth.logout();
    return throwError(() => msg);
};
